import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Globals } from 'app/_globals';
import { TrainingCourseSection } from 'app/_models/training/training-course-section.model';
import { TrainingSectionType } from 'app/_models/training/training-section-type';
import { TrainingType } from 'app/_models/training/training-type';
import { NotificationService } from 'app/_services/notification.service';
import { SiteOwnerUserService } from 'app/_services/site-owner-user.service';
import { EditPdfSectionModalComponent } from '../sections/edit-pdf-section-modal/edit-pdf-section-modal.component';
import { EditVideoSectionModalComponent } from '../sections/edit-video-section-modal/edit-video-section-modal.component';
import { TrainingService } from './../../../_services/training.service';
import Swal from 'sweetalert2';
import { SelectItem } from 'primeng/api';

@Component({
  selector: 'edit-training-page',
  templateUrl: './edit-training-page.component.html',
})
export class EditTrainingPageComponent implements OnInit {

  public trainingId: string;
  public isEditExistingCourse: boolean;
  public routeRole: any;
  public isSiteOwner = false;
  public routeParams: any;

  public trainingForm: FormGroup;
  public sections: TrainingCourseSection[] = [];

  public TrainingSectionType = TrainingSectionType; // allow enum in template
  public loading = false; // shows spinner
  public isSaving = false; // helps prevent double click submit
  public mainSectionIsSaved = false;
  public isReorderingMode = false;
  public orderOptions: SelectItem[] = [];
  public sectionOrder: { courseSectionId: number, order: number }[];

  public optionsType: { label: string; value: TrainingType }[] = [
    { label: 'Induction', value: TrainingType.Induction },
    { label: 'Training', value: TrainingType.Training },
  ];

  public siteOwners: any[] = [];

  constructor(
    private trainingService: TrainingService,
    private siteOwnerUserService: SiteOwnerUserService,
    private modalService: NgbModal,
    private notificationService: NotificationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private globals: Globals
  ) {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.trainingId = params.trainingId;
      this.isEditExistingCourse = !!this.trainingId;
    });
    this.activatedRoute.queryParams.subscribe((params: Params) => {
      this.routeParams = activatedRoute.snapshot.data.breadcrumbs;
    });
    this.routeRole = globals.user.routeRole;

    this.isSiteOwner = this.routeRole === 'site-owner' || this.routeRole === 'power-station-owner';

    if(this.routeRole === 'power-station-owner'){
      this.optionsType.push( { label: 'Outage Acceptance',  value: TrainingType.Outage });
    }
  }

  ngOnInit() {
    this.createEditTrainingForm();

    if (!this.isSiteOwner) {
      this.getSiteOwners().then((result) => {
        if (this.isEditExistingCourse) {
          this.getExistingCourseData();
        }
      });
    } else {
      if (this.isEditExistingCourse) {
        this.getExistingCourseData();
      }
    }
  }

  public getSiteOwners() {
    return new Promise((resolve, reject) => {
      this.siteOwnerUserService.getAllEnabledSiteOwnersForDropdowns().subscribe(
        (data: any) => {
          this.siteOwners = data.map((element) => ({
            label: element.firstName + ' ' + element.lastName,
            value: element.id,
          }));
          resolve('');
        },
        (error) => {
          console.error(error);
          this.notificationService.notify('danger', 'Error', 'Could not fetch list of site owners');
          resolve('');
        }
      );
    });
  }

  // Form

  public createEditTrainingForm() {
    this.trainingForm = this.fb.group({
      courseType: new FormControl('', [Validators.required]),
      name: new FormControl('', [Validators.required]),
      validForMonths: new FormControl('', [Validators.required, Validators.pattern(/^[1-9][0-9]*$/)]),
      description: new FormControl('', [Validators.required]),
      ...(!this.isSiteOwner && { siteOwnerSkylarkId: new FormControl('', [Validators.required]) }),
    });
  }

  private getExistingCourseData() {
    this.trainingService.getTrainingCourseById(this.trainingId).subscribe(
      (course) => {
        this.trainingForm.patchValue({
          courseType: this.optionsType.find((option) => option.value === course.type),
          name: course.trainingName,
          validForMonths: course.validForMonths,
          description: course.description,
          ...(!this.isSiteOwner && { siteOwnerSkylarkId: this.siteOwners.find((siteOwnerOption) => siteOwnerOption.value === course.siteOwner.id) }),
        });

        this.sections = course.sections;

        this.setMainSectionToReadOnly();
      },
      (error) => {
        console.error(error);
        this.notificationService.notify('danger', 'Error', 'Could not load the existing course data');
      }
    );
  }

  public submitMainSectionForm() {
    this.isSaving = true;
    this.submitInductionTraining(this.trainingForm.value);
  }

  public updateMainSection() {
    this.isSaving = true;
    this.submitTrainingCourseUpdate(this.trainingForm.value);
  }

  // Section

  public submitInductionTraining(values: any) {
    const body = {
      courseType: values.courseType.value,
      name: values.name,
      validForMonths: values.validForMonths,
      description: values.description,
      ...(!this.isSiteOwner && { siteOwnerSkylarkId: values.siteOwnerSkylarkId.value }),
    };

    this.trainingService.createNewTrainingCourse(body).subscribe(
      (response) => {
        this.isSaving = false;
        this.trainingId = response.id;

        this.setMainSectionToReadOnly();
        this.router.navigate(['/' + this.routeRole + '/training/' + this.trainingId + '/edit']);
        this.notificationService.notify('success', 'Success', 'Details saved');
      },
      (error) => {
        console.error(error);
        this.isSaving = false;
        throw error;
      }
    );
  }

  public submitTrainingCourseUpdate(values: any) {
    const body = {
      name: values.name,
      validForMonths: values.validForMonths,
      description: values.description,
    };

    this.trainingService.updateTrainingCourse(this.trainingId, body).subscribe(
      (response) => {
        this.isSaving = false;

        this.notificationService.notify('success', 'Success', 'Details saved');
      },
      (error) => {
        console.error(error);
        this.isSaving = false;
        throw error;
      }
    );
  }

  public finishEditing() {
    this.router.navigate(['/' + this.routeRole + '/training/' + this.trainingId]);
  }

  public reorderSections() {
    this.isReorderingMode = true;

    this.sectionOrder = this.sections.map(s => ({
      courseSectionId: s.courseSectionId,
      order: s.sectionOrder,
    }));

    // Make an ordered array of integers
    const orderedIndexes = Array.from({ length: this.sectionOrder.length }, (_, i) => i + 1);
    this.orderOptions = orderedIndexes.map(i => ({ label: 'Section ' + i, value: i}));
  }

  public onDropdownChange(index: number): void {
    const selectedOrder = this.sectionOrder[index].order;
    const takenOrders = this.sectionOrder.map(s => s.order).filter(o => o !== null);

    // update the other section which previously had this order number to the next available number
    for (let i = 1; i <= this.sectionOrder.length; i++) {
      if (!takenOrders.includes(i)) {
        this.sectionOrder
          .filter((s) => s.order === selectedOrder && s !== this.sectionOrder[index])
          .forEach((s) => (s.order = i));
        break;
      }
    }
  }

  public saveOrder() {
    this.loading = true;
    this.isReorderingMode = false;

    const body = this.sectionOrder;

    this.trainingService.reorderSections(this.trainingId, body).subscribe(
      (response: any) => {
        this.loading = false;
        this.notificationService.notify('success', 'Success', 'Order of sections has been updated');
        this.getSections();
      },
      (error) => {
        console.error(error);
        this.loading = false;
        this.notificationService.notify('danger', 'Error', 'Order of sections could not be saved');
      }
    );
  }

  private setMainSectionToReadOnly() {
    this.mainSectionIsSaved = true;
    this.trainingForm.controls.courseType.disable();
    if (!this.isSiteOwner) {
      this.trainingForm.controls.siteOwnerSkylarkId.disable();
    }
  }

  public getSections() {
    this.trainingService.getTrainingCourseById(this.trainingId).subscribe(
      (response) => {
        this.sections = response.sections;
      },
      (error) => {
        console.error(error);
      }
    );
  }

  // Modal

  public addVideoSection() {
    const modalOptions: NgbModalOptions = { centered: true, size: 'lg', backdrop: 'static' };
    const modal = this.modalService.open(EditVideoSectionModalComponent, modalOptions);

    modal.result
      .then((result) => {
        if (result.video) {
          this.submitVideoCourseSection(result.video);
        }
      })
      .catch(() => {
        /* modal dismissed */
      });
  }

  public addPDFSection() {
    const modalOptions: NgbModalOptions = { centered: true, size: 'lg', backdrop: 'static' };
    const modal = this.modalService.open(EditPdfSectionModalComponent, modalOptions);

    modal.result
      .then((result) => {
        if (result.document) {
          this.submitDocumentCourseSection(result.document);
        }
      })
      .catch(() => {
        /* modal dismissed */
      });
  }

  public addQuestionSection(sectionId) {
    const body = {
      courseId: this.trainingId,
      sectionName: 'Questions',
      sectionType: TrainingSectionType.Question,
    };

    this.trainingService.createNewCourseQuestionSection(body).subscribe(
      (response) => {
        this.router.navigate(['/' + this.routeRole + '/training/' + this.trainingId + '/edit/section/' + response.id + '/questions']);
      },
      (error) => {
        console.error(error);
        this.loading = false;
        this.notificationService.notify('danger', 'Error', 'Question section could not be added');
      }
    );
  }

  // Section

  public deleteSection(trainingSectionId) {
    Swal.fire({
      title: 'Delete Training Section?',
      text: 'You will not be able to revert this',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, delete it',
    }).then((result) => {
      if (result.value) {
        this.trainingService.deleteTrainingSection(trainingSectionId).subscribe(
          (response) => {
            this.getSections();
            this.notificationService.notify('success', 'Success', 'Deleted training section');
          },
          (error) => {
            console.error(error);
            this.notificationService.notify('danger', 'Error', 'Could not delete training section');
          }
        );
      }
    });
  }

  public submitDocumentCourseSection(document: any) {
    this.loading = true;

    const body = {
      courseId: this.trainingId,
      sectionName: 'Document',
      sectionType: TrainingSectionType.Pdf,
    };

    this.trainingService.createNewCourseDocumentSection(body, document).subscribe(
      (response: any) => {
        this.loading = false;
        this.notificationService.notify('success', 'Success', 'Document section added');
        this.getSections();
      },
      (error) => {
        console.error(error);
        this.loading = false;
        if (error.message === "Infected"){
          this.notificationService.notify('danger', `Error`, `Document is infected with malware!`);
        } else if (error.message === "EmbeddedScript"){
          this.notificationService.notify('danger', `Error`, `Document has embedded script - please submit file to Skylark for analysis`);
        } else {
          this.notificationService.notify('danger', 'Error', 'Document section could not be added');
        }

      }
    );
  }

  public submitVideoCourseSection(values: any) {
    const body = {
      courseId: this.trainingId,
      sectionName: 'Video',
      sectionType: TrainingSectionType.Video,
      fileURL: values,
    };

    this.trainingService.createNewCourseVideoSection(body).subscribe(
      (response) => {
        this.getSections();
      },
      (error) => {
        console.error(error);
        this.loading = false;
        this.notificationService.notify('danger', 'Error', 'Video section could not be added');
      }
    );
  }
}
